// MARK://dhk8/admin-shell/components/FieldHelp
// Side-panel contextual para un field-doc (catalog-builder review item A2).
// Wrapper que envuelve un <span className="adm-field__label">…</span> y le
// agrega:
//   - tooltip al hover/focus sobre el label (summary corto)
//   - botón "?" al lado del label → abre Sheet con summary + examples +
//     commonMistakes (drawer expandible, sin perder contexto del form).
//
// Si no hay doc registrado para (entity, field), no renderiza el "?" — el
// label se queda solo (degradación silenciosa).
import { useState } from 'react'
import { HelpCircle } from 'lucide-react'
import Sheet from './Sheet.js'
import { getFieldDoc } from '../lib/field-docs.js'

interface FieldHelpProps {
  entity: string
  field: string
  /** Label visible — sin esto, no es útil mostrar la ayuda. */
  label: string
}

export default function FieldHelp({ entity, field, label }: FieldHelpProps) {
  const doc = getFieldDoc(entity, field)
  const [open, setOpen] = useState(false)

  if (!doc) {
    return <span className="adm-field__label">{label}</span>
  }

  return (
    <>
      <span
        className="adm-field__label adm-field__label--with-help"
        title={doc.summary}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        {label}
        <button
          type="button"
          className="adm-field__help-btn"
          aria-label={`Ayuda sobre ${label}`}
          onClick={(e) => { e.preventDefault(); setOpen(true) }}
          tabIndex={0}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            padding: 0,
            margin: 0,
            border: 'none',
            background: 'transparent',
            color: 'var(--pos-fg-tertiary)',
            cursor: 'help',
            borderRadius: 9999,
          }}
        >
          <HelpCircle size={14} aria-hidden />
        </button>
      </span>

      <Sheet
        open={open}
        title={label}
        onClose={() => setOpen(false)}
        footer={<>
          <button className="adm-btn adm-btn--primary" onClick={() => setOpen(false)}>Entendido</button>
        </>}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <section>
            <div className="adm-card__title" style={{ marginBottom: 6 }}>Qué es</div>
            <p style={{ margin: 0, lineHeight: 1.5, color: 'var(--pos-fg-secondary)' }}>{doc.summary}</p>
          </section>
          {doc.examples.length > 0 && (
            <section>
              <div className="adm-card__title" style={{ marginBottom: 6 }}>Ejemplos</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: 'var(--pos-fg-secondary)' }}>
                {doc.examples.map((ex, i) => (<li key={i}><code style={{ fontSize: '0.9em' }}>{ex}</code></li>))}
              </ul>
            </section>
          )}
          {doc.commonMistakes.length > 0 && (
            <section>
              <div className="adm-card__title" style={{ marginBottom: 6 }}>Errores frecuentes</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7, color: 'var(--pos-fg-secondary)' }}>
                {doc.commonMistakes.map((m, i) => (<li key={i}>{m}</li>))}
              </ul>
            </section>
          )}
        </div>
      </Sheet>
    </>
  )
}
